/**
 * API Error Handling Utilities
 *
 * Provides centralized error classification, user-friendly messaging,
 * and retry logic for API errors.
 *
 * Features:
 * - Error type classification (network, rate limit, auth, server, etc.)
 * - User-friendly error messages
 * - Retry recommendations
 * - Error logging utilities
 */

// Error types for classification
export const ErrorTypes = {
  NETWORK: 'NETWORK',
  RATE_LIMIT: 'RATE_LIMIT',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  PLATFORM_LIMIT: 'PLATFORM_LIMIT',
  AUTH: 'AUTH',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER',
  TIMEOUT: 'TIMEOUT',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN'
};

// User-friendly error messages by type
const errorMessages = {
  [ErrorTypes.NETWORK]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    suggestion: 'Check your network and try again.'
  },
  [ErrorTypes.RATE_LIMIT]: {
    title: 'Rate Limit Exceeded',
    message: 'Too many requests. Please wait a moment before trying again.',
    suggestion: 'Wait a few seconds and try again.'
  },
  [ErrorTypes.QUOTA_EXCEEDED]: {
    title: 'Monthly AI allowance used',
    message: 'Your organization has used its monthly AI allowance.',
    suggestion: 'It resets on the 1st of next month. Contact your administrator if you need more before then.'
  },
  [ErrorTypes.PLATFORM_LIMIT]: {
    title: 'AI assistant unavailable',
    message: 'The AI assistant has reached its monthly usage limit.',
    suggestion: 'Please contact Decisiv support.'
  },
  [ErrorTypes.AUTH]: {
    title: 'Authentication Error',
    message: 'Your session may have expired or the API key is invalid.',
    suggestion: 'Try refreshing the page or check your API key settings.'
  },
  [ErrorTypes.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    suggestion: 'The item may have been deleted or moved.'
  },
  [ErrorTypes.SERVER]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Our team has been notified.',
    suggestion: 'Please try again in a few moments.'
  },
  [ErrorTypes.TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long to complete.',
    suggestion: 'Try a shorter message or try again later.'
  },
  [ErrorTypes.VALIDATION]: {
    title: 'Invalid Request',
    message: 'The request could not be processed due to invalid data.',
    suggestion: 'Check your input and try again.'
  },
  [ErrorTypes.UNKNOWN]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    suggestion: 'Please try again or contact support if the problem persists.'
  }
};

/**
 * Classify an error into a specific type
 * @param {Error|Response|Object} error - The error to classify
 * @returns {string} Error type from ErrorTypes
 */
export function classifyError(error) {
  // Network/fetch errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return ErrorTypes.NETWORK;
  }

  // Check for network-related error messages
  if (error.message) {
    const msg = error.message.toLowerCase();
    if (msg.includes('network') || msg.includes('failed to fetch') || msg.includes('net::')) {
      return ErrorTypes.NETWORK;
    }
    if (msg.includes('timeout') || msg.includes('timed out')) {
      return ErrorTypes.TIMEOUT;
    }
    // Server refusal texts: llm/config.js allowanceRefusalMessage().
    if (msg.includes('monthly usage limit')) {
      return ErrorTypes.PLATFORM_LIMIT;
    }
    if (msg.includes('quota exceeded') || msg.includes('llm quota') || msg.includes('monthly ai allowance')) {
      return ErrorTypes.QUOTA_EXCEEDED;
    }
    if (msg.includes('rate limit') || msg.includes('429') || msg.includes('too many requests')) {
      return ErrorTypes.RATE_LIMIT;
    }
    if (msg.includes('unauthorized') || msg.includes('401') || msg.includes('api key')) {
      return ErrorTypes.AUTH;
    }
    if (msg.includes('not found') || msg.includes('404')) {
      return ErrorTypes.NOT_FOUND;
    }
    if (msg.includes('validation') || msg.includes('invalid') || msg.includes('400')) {
      return ErrorTypes.VALIDATION;
    }
  }

  // Check status codes if available
  const status = error.status || error.statusCode;
  if (status) {
    if (status === 429) return ErrorTypes.RATE_LIMIT;
    if (status === 401 || status === 403) return ErrorTypes.AUTH;
    if (status === 404) return ErrorTypes.NOT_FOUND;
    if (status === 408) return ErrorTypes.TIMEOUT;
    if (status === 400 || status === 422) return ErrorTypes.VALIDATION;
    if (status >= 500) return ErrorTypes.SERVER;
  }

  // Check for abort errors
  if (error.name === 'AbortError') {
    return ErrorTypes.TIMEOUT;
  }

  return ErrorTypes.UNKNOWN;
}

/**
 * Get user-friendly error info based on error type
 * @param {string} errorType - Error type from ErrorTypes
 * @returns {Object} Error info with title, message, and suggestion
 */
export function getErrorInfo(errorType) {
  return errorMessages[errorType] || errorMessages[ErrorTypes.UNKNOWN];
}

/**
 * Parse an error into a structured error object with user-friendly messaging
 * @param {Error|Response|Object|string} error - The error to parse
 * @returns {Object} Structured error object
 */
export function parseError(error) {
  // Handle string errors
  if (typeof error === 'string') {
    const type = classifyError({ message: error });
    const info = getErrorInfo(type);
    return {
      type,
      ...info,
      originalMessage: error,
      canRetry: isRetryable(type),
      retryDelay: getRetryDelay(type)
    };
  }

  // Handle error objects
  const type = classifyError(error);
  const info = getErrorInfo(type);
  const originalMessage = error.message || error.error || 'Unknown error';

  return {
    type,
    ...info,
    originalMessage,
    canRetry: isRetryable(type),
    retryDelay: getRetryDelay(type),
    statusCode: error.status || error.statusCode
  };
}

/**
 * Check if an error type is retryable
 * @param {string} errorType - Error type from ErrorTypes
 * @returns {boolean} Whether the error can be retried
 */
export function isRetryable(errorType) {
  return [
    ErrorTypes.NETWORK,
    ErrorTypes.RATE_LIMIT,
    ErrorTypes.SERVER,
    ErrorTypes.TIMEOUT
  ].includes(errorType);
}

/**
 * Get recommended retry delay for an error type
 * @param {string} errorType - Error type from ErrorTypes
 * @returns {number} Delay in milliseconds
 */
export function getRetryDelay(errorType) {
  switch (errorType) {
    case ErrorTypes.RATE_LIMIT:
      return 5000; // 5 seconds for rate limits
    case ErrorTypes.SERVER:
      return 3000; // 3 seconds for server errors
    case ErrorTypes.NETWORK:
    case ErrorTypes.TIMEOUT:
      return 2000; // 2 seconds for network/timeout
    default:
      return 1000; // 1 second default
  }
}

/**
 * Create an error handler with retry logic
 * @param {Function} operation - The async operation to retry
 * @param {Object} options - Retry options
 * @returns {Function} Wrapped function with retry logic
 */
export function withRetry(operation, options = {}) {
  const {
    maxRetries = 3,
    onRetry = () => {},
    shouldRetry = (error) => isRetryable(classifyError(error))
  } = options;

  return async (...args) => {
    let lastError;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await operation(...args);
      } catch (error) {
        lastError = error;
        attempt++;

        if (attempt >= maxRetries || !shouldRetry(error)) {
          throw error;
        }

        const delay = getRetryDelay(classifyError(error));
        onRetry({ attempt, maxRetries, error, delay });
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  };
}

/**
 * Format error for display in UI
 * @param {Error|Object|string} error - The error to format
 * @returns {Object} Formatted error for UI display
 */
export function formatErrorForUI(error) {
  const parsed = parseError(error);

  return {
    title: parsed.title,
    message: parsed.message,
    suggestion: parsed.suggestion,
    canRetry: parsed.canRetry,
    retryDelay: parsed.retryDelay,
    type: parsed.type,
    // Technical details for debugging (hidden from normal users)
    details: {
      originalMessage: parsed.originalMessage,
      statusCode: parsed.statusCode,
      errorType: parsed.type
    }
  };
}

/**
 * Log error for debugging and monitoring
 * @param {Error|Object} error - The error to log
 * @param {Object} context - Additional context
 */
export function logError(error, context = {}) {
  const parsed = parseError(error);

  console.error('[API Error]', {
    type: parsed.type,
    message: parsed.originalMessage,
    ...context,
    timestamp: new Date().toISOString()
  });
}

export default {
  ErrorTypes,
  classifyError,
  getErrorInfo,
  parseError,
  isRetryable,
  getRetryDelay,
  withRetry,
  formatErrorForUI,
  logError
};
